// Live preview panel — renders the wallpaper into a hidden full-size container,
// then shows a CSS-scaled thumbnail. Includes export buttons.

import { store } from "../state/store";
import { renderWallpaper } from "../render/wallpaper";
import { applyAutofit, preloadFonts } from "../render/autofit";
import { exportWallpaperPNG } from "../export/png";
import { buildICS, downloadICS } from "../export/ics";
import { getDevice } from "../data/devices";
import type { FestSet } from "../data/schedule";

export function mountPreview(host: HTMLElement, allSets: FestSet[]): void {
  host.innerHTML = `
    <div class="preview-panel">
      <h3>Preview</h3>
      <div class="preview-stage">
        <div class="preview-frame" id="preview-frame"></div>
      </div>
      <div class="preview-meta" id="preview-meta"></div>
      <div class="preview-actions">
        <button class="primary-btn" id="export-png">⬇ Download Wallpaper PNG</button>
        <button class="primary-btn" id="export-ics">⬇ Download .ics Calendar</button>
        <button class="ghost-btn" id="copy-link">⎘ Copy Share Link</button>
      </div>
      <div class="export-status" id="export-status"></div>
    </div>
    <div class="hidden-render-host" id="hidden-host" aria-hidden="true"></div>
  `;

  const frame = host.querySelector<HTMLElement>("#preview-frame")!;
  const meta = host.querySelector<HTMLElement>("#preview-meta")!;
  const hidden = host.querySelector<HTMLElement>("#hidden-host")!;
  const status = host.querySelector<HTMLElement>("#export-status")!;
  const exportPng = host.querySelector<HTMLButtonElement>("#export-png")!;
  const exportIcs = host.querySelector<HTMLButtonElement>("#export-ics")!;
  const copyLink = host.querySelector<HTMLButtonElement>("#copy-link")!;

  let currentEl: HTMLElement | null = null;
  let renderTimer: number | null = null;

  function scheduleRender() {
    if (renderTimer !== null) clearTimeout(renderTimer);
    renderTimer = window.setTimeout(doRender, 150);
  }

  async function doRender() {
    const s = store.get();
    const device = getDevice(s.deviceId);
    hidden.innerHTML = "";
    const el = renderWallpaper({
      day: s.day,
      stages: s.stages,
      faveIds: s.faves,
      device,
      spotlightFaves: s.spotlightFaves,
      allSets,
    });
    hidden.appendChild(el);
    currentEl = el;

    try {
      await preloadFonts();
    } catch (err) {
      console.warn("font preload failed:", err);
    }
    // Use setTimeout(0) instead of rAF — rAF gets throttled in inactive tabs
    // and headless browsers (e.g. preview rendering during build).
    await new Promise(r => setTimeout(r, 0));
    applyAutofit(el);

    // Show scaled thumbnail in the frame
    const PREVIEW_W = 320;
    const scale = PREVIEW_W / device.w;
    frame.innerHTML = "";
    const wrapper = document.createElement("div");
    wrapper.style.cssText = `width:${PREVIEW_W}px; height:${device.h * scale}px; position:relative; overflow:hidden;`;
    const clone = el.cloneNode(true) as HTMLElement;
    clone.style.transform = `scale(${scale})`;
    clone.style.transformOrigin = "top left";
    wrapper.appendChild(clone);
    frame.appendChild(wrapper);

    // Meta
    const dayPerfs = allSets.filter(p => p.day === s.day && s.stages.includes(p.stage));
    const dayFaves = dayPerfs.filter(p => s.faves.has(p.id));
    meta.innerHTML = `
      <div>${device.label} · ${device.w}×${device.h}</div>
      <div>${dayPerfs.length} sets shown · <strong>${dayFaves.length}</strong> favorited today</div>
    `;
  }

  exportPng.addEventListener("click", async () => {
    if (!currentEl) return;
    const s = store.get();
    const device = getDevice(s.deviceId);
    const dayName = ["fri","sat","sun"][s.day - 1];
    const filename = `EDC2026_${dayName}_${device.id}.png`;
    setStatus("Rendering PNG…");
    exportPng.disabled = true;
    try {
      await exportWallpaperPNG(currentEl, filename);
      setStatus(`Saved ${filename}`);
    } catch (err) {
      console.error(err);
      setStatus(`PNG export failed: ${(err as Error).message}`);
    } finally {
      exportPng.disabled = false;
    }
  });

  exportIcs.addEventListener("click", () => {
    const s = store.get();
    const faves = allSets.filter(p => s.faves.has(p.id));
    if (faves.length === 0) {
      setStatus("Favorite at least one set first.");
      return;
    }
    const ics = buildICS(faves);
    downloadICS(ics, `EDC2026_favorites_${faves.length}sets.ics`);
    setStatus(`Saved ${faves.length} sets + 3 fireworks events`);
  });

  copyLink.addEventListener("click", async () => {
    const url = store.toURL();
    try {
      await navigator.clipboard.writeText(url);
      setStatus("Share link copied to clipboard");
    } catch {
      setStatus(url);
    }
  });

  function setStatus(msg: string) {
    status.textContent = msg;
    setTimeout(() => { if (status.textContent === msg) status.textContent = ""; }, 3500);
  }

  store.subscribe(scheduleRender);
  // Initial render after fonts ready
  preloadFonts().then(scheduleRender);
}
